<template>
    <div class="tw-bg-stone-50 tw-min-h-screen tw-py-16">
        <v-container class="tw-max-w-7xl">
            <div class="tw-text-center tw-mb-24">
                <h1 class="tw-text-5xl tw-font-light tw-text-gray-800 tw-mb-4 tw-tracking-tight">O Nosso <span class="tw-text-pink-400 tw-font-medium">Menu</span></h1>
                <p class="tw-text-gray-500 tw-text-lg tw-max-w-2xl tw-mx-auto">
                    Cada bolo é feito com dedicação e amor. Explore as nossas categorias e encontre a doçura perfeita para a sua próxima celebração.
                </p>
            </div>

            <div
                v-for="(category, index) in categories"
                :key="category.id"
                class="tw-mb-32 last:tw-mb-0">
                <div
                    class="tw-flex tw-flex-col lg:tw-flex-row tw-items-center tw-gap-12 lg:tw-gap-20"
                    :class="index % 2 !== 0 ? 'lg:tw-flex-row-reverse' : ''">
                    <div class="tw-w-full lg:tw-w-1/3 tw-flex tw-flex-col tw-justify-center">
                        <div class="tw-inline-flex tw-items-center tw-gap-2 tw-mb-4">
                            <v-icon
                                icon="mdi-star-four-points-outline"
                                color="pink-lighten-2"
                                size="small"></v-icon>
                            <span class="tw-text-pink-400 tw-uppercase tw-tracking-widest tw-text-sm tw-font-semibold"> Categoria </span>
                        </div>

                        <h2 class="tw-text-4xl tw-font-semibold tw-text-gray-800 tw-mb-6 tw-leading-tight">
                            {{ category.title }}
                        </h2>

                        <p class="tw-text-gray-600 tw-text-lg tw-leading-relaxed tw-mb-10">
                            {{ category.description }}
                        </p>

                        <div>
                            <v-btn
                                color="secondary"
                                variant="flat"
                                size="x-large"
                                rounded="pill"
                                class="tw-font-medium tw-tracking-wide tw-text-primary-100 hover:tw-shadow-lg hover:-tw-translate-y-1 tw-transition-all tw-duration-300"
                                @click="handleOrder(category)">
                                <p class="tw-text-primary-100">Encomendar Agora</p>
                            </v-btn>
                        </div>
                    </div>

                    <div class="tw-w-full lg:tw-w-2/3">
                        <div
                            v-if="category.images.length > 0"
                            class="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 tw-gap-4 lg:tw-gap-6">
                            <div
                                v-for="(imgSrc, imgIndex) in category.images.slice(0, 6)"
                                :key="imgIndex"
                                class="tw-relative tw-group tw-rounded-2xl tw-overflow-hidden tw-shadow-sm hover:tw-shadow-xl tw-transition-all tw-duration-500">
                                <v-img
                                    :src="imgSrc"
                                    height="280"
                                    cover
                                    class="tw-bg-gray-200 group-hover:tw-scale-105 tw-transition-transform tw-duration-700">
                                    <template v-slot:placeholder>
                                        <div class="tw-flex tw-items-center tw-justify-center tw-h-full">
                                            <v-progress-circular
                                                indeterminate
                                                color="pink-lighten-3"></v-progress-circular>
                                        </div>
                                    </template>
                                </v-img>
                                <div class="tw-absolute tw-inset-0 tw-bg-pink-900/10 tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity tw-duration-300"></div>
                            </div>
                        </div>

                        <div
                            v-else
                            class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-[280px] tw-bg-white tw-rounded-3xl tw-border-2 tw-border-dashed tw-border-pink-100">
                            <v-icon
                                icon="mdi-camera-outline"
                                color="pink-lighten-3"
                                size="64"
                                class="tw-mb-4"></v-icon>
                            <p class="tw-text-gray-400 tw-text-lg">Novas doçuras a caminho...</p>
                        </div>
                    </div>
                </div>

                <div
                    v-if="index !== categories.length - 1"
                    class="lg:tw-hidden tw-w-24 tw-h-1 tw-bg-pink-100 tw-rounded-full tw-mx-auto tw-mt-24"></div>
            </div>
        </v-container>
    </div>
</template>

<script lang="ts" setup>
    import { ref } from 'vue';
    import { useRouter } from 'vue-router';
    const router = useRouter();

    // (Mantém as tuas importações de imagens iguais)
    const bentoImages = import.meta.glob('@/assets/images/bento_cakes/*.{png,jpg,jpeg,webp}', { eager: true, import: 'default' });
    const floralImages = import.meta.glob('@/assets/images/floral_cakes/*.{png,jpg,jpeg,webp}', { eager: true, import: 'default' });
    const sugarPasteImages = import.meta.glob('@/assets/images/sugar_paste_cakes/*.{png,jpg,jpeg,webp}', { eager: true, import: 'default' });
    const toppersImages = import.meta.glob('@/assets/images/toppers_cakes/*.{png,jpg,jpeg,webp}', { eager: true, import: 'default' });
    const personalizedImages = import.meta.glob('@/assets/images/personalized_cakes/*.{png,jpg,jpeg,webp}', { eager: true, import: 'default' });
    const vintageImages = import.meta.glob('@/assets/images/vintage_cakes/*.{png,jpg,jpeg,webp}', { eager: true, import: 'default' });

    const getImages = (globObj: Record<string, unknown>) => Object.values(globObj) as string[];

    const categories = ref([
        {
            id: 'bento',
            title: 'Bento Cakes',
            description:
                'Pequenos, fofos e cheios de personalidade. Os Bento Cakes são mini bolos perfeitos para presentear alguém especial ou celebrar pequenas conquistas. Vêm na sua própria caixinha com mensagens divertidas ou carinhosas.',
            images: getImages(bentoImages),
        },
        {
            id: 'florais',
            title: 'Bolos Florais',
            description:
                'A elegância da natureza na sua mesa. Bolos decorados com arranjos florais, perfeitos para casamentos, batizados ou aniversários que pedem um toque de romantismo e sofisticação.',
            images: getImages(floralImages),
        },
        {
            id: 'pasta_acucar',
            title: 'Pasta de Açúcar',
            description:
                'Verdadeiras obras de arte comestíveis. Utilizamos pasta de açúcar para esculpir detalhes, figuras e cenários incríveis, transformando o seu bolo numa peça central inesquecível.',
            images: getImages(sugarPasteImages),
        },
        {
            id: 'toppers',
            title: 'Bolos com Toppers',
            description:
                'Bolos deliciosos finalizados com topos personalizados. Desde o nome do aniversariante a pequenas figuras temáticas, os toppers dão um destaque único e moderno à sua celebração.',
            images: getImages(toppersImages),
        },
        {
            id: 'personalizados',
            title: 'Personalizados',
            description: 'Se tem uma ideia, nós transformamo-la num bolo! Cores, temas e formatos à sua escolha. Desafie-nos a criar um bolo que conte a sua história de forma deliciosa.',
            images: getImages(personalizedImages),
        },
        {
            id: 'vintage',
            title: 'Bolos Vintage',
            description: 'O charme do passado com o sabor do presente. Bolos com decorações clássicas, detalhes minuciosos em creme e aquele toque de nostalgia elegante que está super em alta.',
            images: getImages(vintageImages),
        },
    ]);

    const handleOrder = (category: any) => {
        console.log(`Iniciar encomenda para a categoria: ${category.title}`);
        router.push({ name: 'Order' });
    };
</script>
